import { type FC, useMemo, useState } from "react";

import type {
  PlanSnapshotGroupedColumn,
  PlanSnapshotNodeWithDependencyCounts,
} from "../../types";

import {
  type PlanSnapshotViewMode,
  usePlanSnapshots,
} from "../../hooks/usePlanSnapshots";
import { formatRelativeTime } from "../../utils/time";
import { Button, DataState, EmptyState } from "../common";
import { FormInput } from "../common/forms/FormInput";
import { FormSelect } from "../common/forms/FormSelect";
import { PlanSnapshotDagView } from "./PlanSnapshotDagView";
import { PlanSnapshotDrawer } from "./PlanSnapshotDrawer";
import { PlanSnapshotKanbanColumn } from "./PlanSnapshotKanbanColumn";

function normalizeSearch(value: string): string {
  return value.trim().toLowerCase();
}

function matchesNode(
  node: PlanSnapshotNodeWithDependencyCounts,
  query: string,
): boolean {
  if (!query) {
    return true;
  }
  const haystack = [
    node.issue_key,
    node.title ?? "",
    node.humanized_description ?? "",
    node.description ?? "",
    node.status ?? "",
    node.classification,
    node.repository_slug ?? "",
  ]
    .join(" ")
    .toLowerCase();
  return haystack.includes(query);
}

function buildFilteredColumns(
  columns: PlanSnapshotGroupedColumn[],
  query: string,
): PlanSnapshotGroupedColumn[] {
  if (!query) {
    return columns;
  }
  return columns.map((column) => ({
    ...column,
    nodes: column.nodes.filter((node) => matchesNode(node, query)),
  }));
}

const SnapshotSearchInput: FC<{
  onChange: (value: string) => void;
  search: string;
}> = ({ onChange, search }) => (
  <FormInput
    label="Search"
    value={search}
    onChange={(event) => onChange(event.target.value)}
    placeholder="Search issue key, title, status…"
  />
);

const QueryIndicator: FC<{ query: string }> = ({ query }) => (
  <>
    <span className="mx-1 text-slate-300 dark:text-slate-600">|</span>
    <span className="text-emerald-600 dark:text-emerald-300">
      filter={query}
    </span>
  </>
);

const PlanSnapshotsToolbar: FC<{
  loading: boolean;
  onRefresh: () => void;
  onSearchChange: (value: string) => void;
  onSelectSnapshot: (value: string) => void;
  search: string;
  snapshotId: string | null;
  snapshotOptions: { label: string; value: string }[];
  snapshotsCount: number;
  viewMode: PlanSnapshotViewMode;
  onViewModeChange: (mode: PlanSnapshotViewMode) => void;
}> = ({
  loading,
  onRefresh,
  onSearchChange,
  onSelectSnapshot,
  search,
  snapshotId,
  snapshotOptions,
  snapshotsCount,
  viewMode,
  onViewModeChange,
}) => (
  <div className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50 px-3 py-3 dark:border-slate-700 dark:bg-arti-panel lg:px-8">
    <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:gap-4">
      <div className="w-full sm:w-72">
        <FormSelect
          label="Snapshot"
          value={snapshotId ?? ""}
          options={snapshotOptions}
          onChange={(event) => onSelectSnapshot(event.target.value)}
          disabled={snapshotsCount === 0}
        />
      </div>
      <div className="flex flex-1 items-end gap-3">
        <div className="flex-1 sm:max-w-md">
          <SnapshotSearchInput search={search} onChange={onSearchChange} />
        </div>
        <ViewToggle value={viewMode} onChange={onViewModeChange} />
        <Button
          variant="secondary"
          size="sm"
          onClick={onRefresh}
          disabled={loading}
        >
          Refresh
        </Button>
      </div>
    </div>
  </div>
);

export const PlanSnapshotsView: FC = () => {
  const {
    loading,
    error,
    snapshots,
    snapshot,
    snapshotId,
    selectedNodeId,
    selectedAttachment,
    viewMode,
    columns,
    refresh,
    selectSnapshot,
    selectNode,
    selectAttachment,
    setViewMode,
    nodeById,
    nodeByIssueKey,
    dependentsByNodeId,
  } = usePlanSnapshots();

  const [search, setSearch] = useState("");

  const query = useMemo(() => normalizeSearch(search), [search]);
  const filteredColumns = useMemo(
    () => buildFilteredColumns(columns, query),
    [columns, query],
  );
  const matchedNodeCount = useMemo(
    () => filteredColumns.reduce((acc, column) => acc + column.nodes.length, 0),
    [filteredColumns],
  );

  const snapshotOptions = useMemo(() => {
    if (snapshots.length === 0) {
      return [{ label: "No snapshots", value: "" }];
    }

    return [
      { label: "Select a snapshot…", value: "" },
      ...snapshots.map((entry) => ({
        label: `${entry.story_issue_key} · v${entry.version} · ${formatRelativeTime(entry.updated_at)}`,
        value: entry.snapshot_id,
      })),
    ];
  }, [snapshots]);

  const selectedNode = selectedNodeId
    ? (nodeById.get(selectedNodeId) ?? null)
    : null;

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-4">
        {/* Toolbar */}
        <PlanSnapshotsToolbar
          loading={loading}
          onRefresh={() => void refresh()}
          onSearchChange={setSearch}
          onSelectSnapshot={(value) => {
            if (value) {
              selectSnapshot(value);
            }
          }}
          search={search}
          snapshotId={snapshotId}
          snapshotOptions={snapshotOptions}
          snapshotsCount={snapshots.length}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
        />

        {/* Board */}
        <DataState
          loading={loading}
          error={error}
          empty={!snapshot?.nodes || snapshot.nodes.length === 0}
          emptyTitle="No plan snapshot nodes"
          emptyDescription="Select a snapshot with a task tree, or try refreshing."
          className="flex min-h-0 flex-1 flex-col gap-4"
          emptyComponent={
            <div className="px-3 lg:px-8">
              <EmptyState
                showRobot
                title="No plan snapshot nodes"
                description="Select a snapshot with a task tree, or try refreshing."
                action={
                  <Button
                    variant="secondary"
                    onClick={() => void refresh()}
                    disabled={loading}
                  >
                    Refresh
                  </Button>
                }
              />
            </div>
          }
        >
          {/* Info bar */}
          <div className="flex flex-wrap items-center justify-between gap-3 px-3 lg:px-8">
            <div className="flex items-center gap-2 font-mono text-[11px] text-slate-500 dark:text-slate-400">
              <span className="h-1.5 w-1.5 bg-emerald-500" />
              <span className="font-bold">
                {matchedNodeCount.toLocaleString()}
              </span>
              <span>nodes</span>
              {query ? <QueryIndicator query={query} /> : null}
            </div>
            {snapshot ? (
              <span className="font-mono text-[11px] text-slate-400 dark:text-slate-500">
                {snapshot.story_issue_key}
                <span className="mx-1 text-slate-300 dark:text-slate-600">
                  ::
                </span>
                v{snapshot.version}
              </span>
            ) : null}
          </div>

          {viewMode === "kanban" ? (
            <div className="flex min-h-0 flex-1 snap-x snap-mandatory scroll-pl-3 gap-4 overflow-x-auto overflow-y-hidden px-3 pb-4 pt-1 sm:snap-none lg:scroll-pl-8 lg:px-8">
              {filteredColumns.map((column) => (
                <PlanSnapshotKanbanColumn
                  key={column.classification}
                  column={column}
                  query={query}
                  onSelectNode={(node) => selectNode(node.node_id)}
                />
              ))}
              {filteredColumns.length === 0 ? (
                <div className="min-w-[240px] px-3 sm:min-w-[340px] lg:px-8">
                  <EmptyState
                    size="sm"
                    title="No columns to display"
                    description="This snapshot does not include a classified task tree."
                    showRobot
                  />
                </div>
              ) : null}
            </div>
          ) : (
            <PlanSnapshotDagView
              nodes={Array.from(nodeById.values())}
              nodeById={nodeById}
              nodeByIssueKey={nodeByIssueKey}
              onSelectNode={(nodeId) => selectNode(nodeId)}
            />
          )}
        </DataState>

        <PlanSnapshotDrawer
          open={Boolean(selectedNode)}
          node={selectedNode}
          snapshot={snapshot}
          nodeById={nodeById}
          nodeByIssueKey={nodeByIssueKey}
          dependentsByNodeId={dependentsByNodeId}
          selectedAttachment={selectedAttachment}
          onClose={() => selectNode(null)}
          onSelectNode={(nodeId) => selectNode(nodeId)}
          onSelectAttachment={selectAttachment}
        />
      </div>
    </div>
  );
};

const VIEW_OPTIONS: { value: PlanSnapshotViewMode; label: string }[] = [
  { label: "Board", value: "kanban" },
  { label: "Graph", value: "dag" },
];

const ViewToggle: FC<{
  value: PlanSnapshotViewMode;
  onChange: (mode: PlanSnapshotViewMode) => void;
}> = ({ value, onChange }) => (
  <div className="inline-flex h-8 items-center border border-slate-300 bg-slate-100 dark:border-slate-700 dark:bg-arti-panel">
    {VIEW_OPTIONS.map((option) => (
      <button
        key={option.value}
        type="button"
        onClick={() => onChange(option.value)}
        className={`flex h-full items-center px-4 font-mono text-[10px] font-bold uppercase tracking-widest transition-colors ${
          value === option.value
            ? "bg-white text-emerald-600 dark:bg-arti-background dark:text-emerald-400"
            : "text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
        }`}
      >
        {option.label}
      </button>
    ))}
  </div>
);

export default PlanSnapshotsView;
